/**
 * ViewTypeBadge - Visual indicator for view type (Virtual vs Materialized)
 *
 * Displays a color-coded badge representing the view type.
 * Color scheme:
 * - virtual: Purple - No storage, queries files directly
 * - materialized: Teal - Pre-computed results stored in Parquet
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Database, HardDrive } from 'lucide-react';
import type { ViewType } from '@/types/views';

interface ViewTypeBadgeProps {
  /**
   * View type to display
   */
  type: ViewType;
  /**
   * Whether to show an icon
   */
  showIcon?: boolean;
  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * Get custom color classes for view type badge
 */
const getColorClasses = (type: ViewType): string => {
  switch (type) {
    case 'virtual':
      return 'bg-violet/10 text-violet border-violet/30';
    case 'materialized':
      return 'bg-teal/10 text-teal border-teal/30';
    default:
      return '';
  }
};

/**
 * Get descriptive tooltip text for view type
 */
const getTooltipText = (type: ViewType): string => {
  switch (type) {
    case 'virtual':
      return 'Virtual view - queries data directly from files without additional storage';
    case 'materialized':
      return 'Materialized view - pre-computed results stored in Parquet for faster queries';
    default:
      return '';
  }
};

/**
 * Get display label for type
 */
const getTypeLabel = (type: ViewType): string => {
  switch (type) {
    case 'virtual':
      return 'Virtual';
    case 'materialized':
      return 'Materialized';
    default:
      return type;
  }
};

/**
 * ViewTypeBadge Component
 *
 * Renders a styled badge indicating the view type (virtual or materialized).
 *
 * @example
 * ```tsx
 * <ViewTypeBadge type="virtual" />
 * <ViewTypeBadge type="materialized" showIcon className="ml-2" />
 * ```
 */
export const ViewTypeBadge: React.FC<ViewTypeBadgeProps> = ({
  type,
  showIcon = false,
  className,
}) => {
  const colorClasses = getColorClasses(type);
  const tooltipText = getTooltipText(type);
  const label = getTypeLabel(type);
  // Resolved as a direct conditional (not a helper call) so the identifier
  // feeding the JSX tag below is never assigned via a CallExpression --
  // react-hooks/static-components flags a dynamic component created during
  // render (#1123).
  const Icon = showIcon
    ? type === 'virtual'
      ? Database
      : type === 'materialized'
        ? HardDrive
        : null
    : null;

  return (
    <Badge
      variant="outline"
      className={`${colorClasses} border font-semibold ${className || ''}`}
      title={tooltipText}
    >
      {Icon && <Icon className="h-3 w-3 mr-1" />}
      {label}
    </Badge>
  );
};
